import dayjs from "dayjs";

const DAY_MS = 86400000;
// Backend stores date-only values as UTC midnight ("2026-11-02T00:00:00.000Z");
// parsing those with the local timezone would shift them to the previous day.
export const dateOnly = (v) => {
  if (!v) return null;
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  return dayjs(v).format("YYYY-MM-DD");
};
const toDay = (d) => dayjs(dateOnly(d)).startOf("day");
const diffDays = (a, b) => Math.round((toDay(a).valueOf() - toDay(b).valueOf()) / DAY_MS);
const iso = (d) => dayjs(d).format("YYYY-MM-DD");
const fmt = (d) => toDay(d).format("DD/MM/YYYY");
// Monday-based week start, independent of dayjs locale.
const mondayOf = (d) => toDay(d).subtract((toDay(d).day() + 6) % 7, "day");

export const HITO_ESTADOS = [
  { value: "pendiente", label: "Pendiente", color: "#8c8c8c" },
  { value: "en_curso", label: "En curso", color: "#1677ff" },
  { value: "hecho", label: "Hecho", color: "#52c41a" },
];

export const estadoHitoMeta = (estado) =>
  HITO_ESTADOS.find((e) => e.value === estado) || HITO_ESTADOS[0];

export function validateHitos(hitos = []) {
  const errors = [];
  hitos.forEach((h, i) => {
    const n = i + 1;
    if (!h?.nombre || !String(h.nombre).trim()) {
      errors.push(`Hito ${n}: el nombre es obligatorio`);
    } else if (!h.inicio || !h.fin) {
      errors.push(`Hito ${n}: indica fecha de inicio y de fin`);
    } else if (toDay(h.fin).isBefore(toDay(h.inicio))) {
      errors.push(`Hito ${n}: la fecha de fin no puede ser anterior al inicio`);
    }
  });
  return { ok: errors.length === 0, errors };
}

export function moveHito(hitos, index, dir) {
  const target = index + dir;
  if (target < 0 || target >= hitos.length) return hitos;
  const next = hitos.slice();
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function computeTimelineLayout(
  hitos = [],
  { hoy, fechaComprometida, fechaVigente } = {}
) {
  const valid = hitos.filter((h) => h?.inicio && h?.fin);
  const points = [];
  valid.forEach((h) => points.push(toDay(h.inicio), toDay(h.fin)));
  if (fechaComprometida) points.push(toDay(fechaComprometida));
  if (fechaVigente) points.push(toDay(fechaVigente));
  if (!points.length) {
    return {
      rangeStart: null, totalDays: 0, weeks: [], bars: [],
      todayPct: null, comprometidaPct: null, vigentePct: null,
    };
  }
  const min = points.reduce((a, b) => (b.isBefore(a) ? b : a));
  const max = points.reduce((a, b) => (b.isAfter(a) ? b : a));
  const start = mondayOf(min);
  const weekCount = Math.floor(diffDays(max, start) / 7) + 1;
  const totalDays = weekCount * 7;
  const pct = (d) => (diffDays(d, start) / totalDays) * 100;
  const weeks = Array.from({ length: weekCount }, (_, i) => {
    const s = start.add(i * 7, "day");
    return { start: iso(s), label: s.format("DD/MM") };
  });
  const bars = valid.map((h) => ({
    id: h._id,
    leftPct: pct(h.inicio),
    widthPct: ((diffDays(h.fin, h.inicio) + 1) / totalDays) * 100,
    estado: h.estado,
  }));
  const marker = (d) => {
    if (!d) return null;
    const p = pct(d);
    return p < 0 || p > 100 ? null : p;
  };
  return {
    rangeStart: iso(start),
    totalDays,
    weeks,
    bars,
    todayPct: marker(hoy || new Date()),
    comprometidaPct: marker(fechaComprometida),
    vigentePct: marker(fechaVigente),
  };
}

const META = {
  a_tiempo: { label: "A tiempo", color: "green" },
  por_vencer: { label: "Por vencer", color: "orange" },
  atrasado: { label: "Atrasado", color: "red" },
  sin_fecha: { label: "Sin fecha", color: "gold" },
  entregado_a_tiempo: { label: "Entregado a tiempo", color: "green" },
  entregado_tarde: { label: "Entregado tarde", color: "red" },
};

export const cumplimientoMeta = (estado) => META[estado] || META.sin_fecha;

export function cumplimientoLabel(c) {
  if (!c || !c.estado || c.estado === "sin_fecha") return "Sin fecha de entrega";
  const base = META[c.estado]?.label || "Sin fecha";
  if ((c.estado === "atrasado" || c.estado === "entregado_tarde") && c.diasAtraso > 0) {
    return `${base}: ${c.diasAtraso} ${c.diasAtraso === 1 ? "día" : "días"} de atraso`;
  }
  return base;
}

export function buildCronogramaText({
  orden, cliente, sede, fechaComprometida, fechaVigente, hitos = [],
}) {
  const lines = [`*Cronograma orden ${orden}*`];
  const who = [cliente, sede].filter(Boolean).join(" · ");
  if (who) lines.push(who);
  if (fechaComprometida) lines.push(`Entrega comprometida: ${fmt(fechaComprometida)}`);
  if (fechaVigente && fechaComprometida && dateOnly(fechaVigente) !== dateOnly(fechaComprometida)) {
    lines.push(`Nueva fecha de entrega: ${fmt(fechaVigente)}`);
  }
  if (hitos.length) lines.push("");
  hitos.forEach((h, i) => {
    lines.push(
      `${i + 1}. ${h.nombre}: ${fmt(h.inicio)} al ${fmt(h.fin)} (${estadoHitoMeta(h.estado).label})`
    );
  });
  return lines.join("\n");
}

const ERROR_MESSAGES = {
  MOTIVO_REQUIRED: "Para reprogramar debes escribir el motivo.",
  MOTIVO_TOO_LONG: "El motivo es demasiado largo (máximo 500 caracteres).",
  FECHA_INVALIDA: "La fecha de entrega no es válida.",
  FECHA_PASADA: "La fecha de entrega no puede estar en el pasado.",
  ORDER_CLOSED_OR_FINALIZED: "La orden ya está entregada, cerrada o finalizada y no se puede modificar.",
  ENTREGA_YA_FIJADA: "Otra persona ya fijó la fecha de entrega. Recarga la orden para verla.",
  ENTREGA_CAMBIO_CONCURRENTE: "La entrega cambió mientras guardabas. Recarga la orden e inténtalo de nuevo.",
  ENTREGA_REQUIRED: "Primero fija la fecha de entrega comprometida.",
  CRONOGRAMA_VACIO: "La orden aún no tiene cronograma para enviar. Guarda al menos un hito.",
  OUTLOOK_NOT_CONNECTED: "No tienes una cuenta de Outlook conectada. Conéctala para enviar correos.",
  OUTLOOK_RECONNECT_REQUIRED: "Tu conexión con Outlook expiró. Vuelve a conectar tu cuenta de Outlook.",
};
const WITH_DETAIL = {
  HITOS_INVALID: "Revisa los hitos",
  EMAIL_INVALID: "Revisa los correos",
};

// Friendly Spanish message for a backend `{code, message}` error. Falls back
// to the backend message, then to `fallback`.
export function entregaErrorMessage(error, fallback) {
  const data = error?.response?.data;
  const code = data?.code;
  if (ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  if (WITH_DETAIL[code]) return data.message ? `${WITH_DETAIL[code]}: ${data.message}` : WITH_DETAIL[code];
  return data?.message || fallback;
}
